import { Autocomplete, Grid, TextField } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { listPrinterModels } from '../../grpc/printerModelsApi';

const CATALOG_PAGE_SIZE = 500;

function catalogData(response) {
    return response?.data || {};
}

export default function PrinterCatalogSelect({
    brand,
    printerModelUuid,
    onChange,
    errors = {},
}) {
    const [brands, setBrands] = useState([]);
    const [items, setItems] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(true);
    const [loadingModels, setLoadingModels] = useState(false);

    useEffect(() => {
        let cancelled = false;
        listPrinterModels({ page: 1, limit: 1 })
            .then((response) => {
                if (cancelled) {
                    return;
                }
                const list = catalogData(response).brands || [];
                setBrands(
                    [...list]
                        .map((value) => String(value || '').trim())
                        .filter(Boolean)
                        .sort((a, b) => a.localeCompare(b, 'pt-BR')),
                );
            })
            .catch(() => {
                if (!cancelled) {
                    setBrands([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingBrands(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const selectedBrand = String(brand || '').trim();
        if (!selectedBrand) {
            setItems([]);
            setLoadingModels(false);
            return undefined;
        }

        let cancelled = false;
        setLoadingModels(true);
        listPrinterModels({ page: 1, limit: CATALOG_PAGE_SIZE, brand: selectedBrand })
            .then((response) => {
                if (!cancelled) {
                    setItems(catalogData(response).items || []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setItems([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingModels(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [brand]);

    const models = useMemo(() => {
        const selectedBrand = String(brand || '').trim().toLowerCase();
        return items
            .filter((item) => String(item.brand || '').trim().toLowerCase() === selectedBrand)
            .sort((a, b) => String(a.model || '').localeCompare(String(b.model || ''), 'pt-BR'));
    }, [items, brand]);

    const selectedModel = useMemo(
        () => models.find((item) => item.uuid === printerModelUuid || item.id === printerModelUuid) || null,
        [models, printerModelUuid],
    );

    const renderError = (field) => {
        if (!errors[field]) {
            return null;
        }
        return (
            <div className="text-danger mt-1" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                {errors[field]}
            </div>
        );
    };

    return (
        <>
            <Grid item xs={12} sm={6}>
                <Autocomplete
                    options={brands}
                    loading={loadingBrands}
                    value={brand || null}
                    isOptionEqualToValue={(option, value) => option === value}
                    onChange={(_, value) => {
                        onChange({
                            brand: value || '',
                            model: '',
                            printer_model_uuid: '',
                        });
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label="Marca" />
                    )}
                />
                {renderError('brand')}
            </Grid>
            <Grid item xs={12} sm={6}>
                <Autocomplete
                    options={models}
                    loading={loadingModels}
                    disabled={!brand}
                    value={selectedModel}
                    getOptionLabel={(option) => option?.model || ''}
                    isOptionEqualToValue={(option, value) => option?.uuid === value?.uuid}
                    onChange={(_, value) => {
                        onChange({
                            brand: brand || value?.brand || '',
                            model: value?.model || '',
                            printer_model_uuid: value?.uuid || value?.id || '',
                            avg_consumption_watts: value?.avg_consumption_watts,
                            max_consumption_watts: value?.max_consumption_watts,
                        });
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label="Modelo" />
                    )}
                />
                {renderError('model')}
                {renderError('printer_model_uuid')}
            </Grid>
        </>
    );
}
